import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, ChevronRight, Circle, LayoutDashboard, Users, Settings, HelpCircle, LogOut, Grid, PlusCircle, Folder, FileText, FileCheck, Inbox, X, BookOpen, ClipboardCheck, ClipboardList, PenTool, Wallet } from 'lucide-react';
import TetfundLogo from '../../../assets/images/TetfundLogo.svg';
import { menuService } from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import { useMenuContext } from '../../sharedComponents/MenuContext';

export const MENU_GROUPS = ['Settings', 'Users', 'Assignments', 'Submissions', 'Assessment Reports', 'Official Reports', 'Assessments', 'Payments'] as const;
type MenuGroup = typeof MENU_GROUPS[number];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

type ApiMenu = {
  id: number;
  displayName: string;
  route: string;
  group: string;
};

type GroupedMenus = Record<string, ApiMenu[]>;

export const GROUP_ICONS: Record<string, React.ComponentType<any>> = {
  'Settings': Settings,
  'Users': Users,
  'Assignments': PenTool,
  'Submissions': Inbox,
  'Assessment Reports': FileText,
  'Official Reports': FileCheck,
  'Assessments': ClipboardCheck,
  'Payments': Wallet,
};

export const AUTHOR_MENUS = [
  { id: 'author-dashboard', label: 'Dashboard', path: '/author', icon: Grid },
  { id: 'create-manuscript', label: 'Submission of Manuscript', path: '/author/create-manuscript', icon: BookOpen },
  { id: 'submission-journal', label: 'Submission of journal', path: '/author/application-for-journal-support', icon: FileText },
  { id: 'my-submissions', label: 'My Submissions', path: '/author/submissions', icon: Folder },
  { id: 'library', label: 'Library', path: '/author/library', icon: BookOpen },
];

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { adminMenus: menus, menusLoading: loading } = useMenuContext();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isAuthor = user?.role === 'author';

  const groupedMenus: GroupedMenus = MENU_GROUPS.reduce((acc, group) => {
    const items = menus
      .filter(menu => menu.group === group)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    if (items.length > 0) acc[group] = items;
    return acc;
  }, {} as GroupedMenus);

  const toggleGroup = (group: string) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('menus');
    if (logout) logout();
  };

  const handleLinkClick = () => {
    if (isMobile && onMobileClose) onMobileClose();
  };

  return (
    <aside
      id="tour-sidebar"
      className={`fixed left-0 top-0 h-screen bg-[#0F172A] flex flex-col transition-all duration-300 ease-in-out z-50 ${isMobile
        ? `w-64 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`
        : collapsed
          ? 'w-[5.4rem]'
          : 'w-64'
        }`}
    >
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-700">
        {(!collapsed || isMobile) && (
          <img src={TetfundLogo} alt="Logo" width="140" className="sidebar-logo bg-white p-1 rounded" />
        )}
        <button
          className={`p-2 text-gray-400 hover:text-white ${!isMobile && collapsed ? 'mx-auto' : ''}`}
          onClick={isMobile && mobileOpen ? onMobileClose : onToggle}
        >
          {isMobile && mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-1 py-3 overflow-y-auto">
        {isAuthor ? (
          <>
            {AUTHOR_MENUS.filter(item => item.id === 'library' || (user?.menus || []).includes(item.id)).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  id={`tour-nav-${item.id}`}
                  key={item.id}
                  to={item.path}
                  onClick={handleLinkClick}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${active
                    ? 'bg-[#15803D] text-white border-l-4 border-white'
                    : 'text-gray-300 hover:bg-[#2a3847] hover:text-white'
                    } ${collapsed && !isMobile ? 'justify-center' : ''}`}
                  title={item.label}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(!collapsed || isMobile) && <span className="font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </>
        ) : (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-6 w-6 text-gray-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : (
              <>
                <Link
                  id="tour-nav-dashboard"
                  to="/admin"
                  onClick={handleLinkClick}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${isActive('/admin')
                    ? 'bg-[#15803D] text-white border-l-4 border-white'
                    : 'text-gray-300 hover:bg-[#2a3847] hover:text-white'
                    } ${collapsed && !isMobile ? 'justify-center' : ''}`}
                  title="Dashboard"
                >
                  <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                  {(!collapsed || isMobile) && <span className="font-medium">Dashboard</span>}
                </Link>

                {Object.entries(groupedMenus).map(([group, items]) => {
                  const GroupIcon = GROUP_ICONS[group] || Settings;
                  const isGroupActive = items.some(item => isActive(`/${item.route}`));

                  return (
                    <div key={group}>
                      <button
                        id={`tour-group-${group.toLowerCase()}`}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${isGroupActive
                          ? 'bg-[#15803D] text-white border-l-4 border-white'
                          : 'text-gray-300 hover:bg-[#2a3847] hover:text-white'
                          } ${collapsed && !isMobile ? 'justify-center' : 'justify-between'}`}
                        onClick={() => toggleGroup(group)}
                      >
                        <div className="flex items-center gap-3">
                          <GroupIcon className="w-5 h-5 flex-shrink-0" />
                          {(!collapsed || isMobile) && <span className="font-medium">{group}</span>}
                        </div>
                        {(!collapsed || isMobile) &&
                          (openGroups[group] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)
                        }
                      </button>

                      {openGroups[group] && (!collapsed || isMobile) && (
                        <div className="ml-8 mt-2 mr-2">
                          {items.map(item => {
                            const path = `/${item.route}`;
                            const active = isActive(path);
                            return (
                              <Link
                                id={`tour-menu-${item.route.replace(/\//g, '-')}`}
                                key={item.id}
                                to={path}
                                onClick={handleLinkClick}
                                className={`flex items-center mt-1 gap-3 px-3 py-2 rounded-lg transition-colors ${active
                                  ? 'bg-[#15803D] text-white'
                                  : 'text-gray-300 hover:bg-[#2a3847] hover:text-white'
                                  }`}
                                title={item.displayName}
                              >
                                <Circle className="w-2 h-2 flex-shrink-0" />
                                <span className="text-sm">{item.displayName}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-700 p-3">
        <Link
          to={isAuthor ? '/author/guide' : '/admin/guide'}
          onClick={handleLinkClick}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors mb-1 ${isActive(isAuthor ? '/author/guide' : '/admin/guide')
            ? 'bg-[#15803D] text-white'
            : 'text-gray-300 hover:bg-[#2a3847] hover:text-white'
            } ${collapsed && !isMobile ? 'justify-center' : ''}`}
          title="Help / Guide"
        >
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          {(!collapsed || isMobile) && <span className="font-medium">Help / Guide</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-red-400 hover:bg-[#2a3847] hover:text-red-300 ${collapsed && !isMobile ? 'justify-center' : ''
            }`}
          title="Sign Out"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {(!collapsed || isMobile) && <span className="font-medium">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;